import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useTranslation } from "react-i18next";
import { setLanguage, type Lang } from "@/i18n";
import i18n from "@/i18n";
import { PublicFooter } from "@/components/PublicFooter";

export const Route = createFileRoute("/")({
  head: () => ({
    meta: [
      { title: "VISIKT — Subscriptions, salary, and real purchasing power" },
      {
        name: "description",
        content:
          "See where your money goes. VISIKT tracks recurring subscriptions, salary growth, and inflation-adjusted purchasing power. Built for Norway.",
      },
      {
        property: "og:title",
        content: "VISIKT — Subscriptions, salary, and real purchasing power",
      },
      {
        property: "og:description",
        content:
          "Track recurring costs, salary growth, and real purchasing power in one Norway-first app.",
      },
      { property: "og:url", content: "https://visikt.com/" },
      { property: "og:type", content: "website" },
    ],
    links: [{ rel: "canonical", href: "https://visikt.com/" }],
  }),
  component: Landing,
});

function Landing() {
  const { t } = useTranslation();
  const [initialT] = useState(() => i18n.getFixedT("en"));
  const [isHydrated, setIsHydrated] = useState(false);
  useEffect(() => {
    setIsHydrated(true);
  }, []);
  const landingT = isHydrated ? t : initialT;
  const navigate = useNavigate();
  const [displayLang, setDisplayLang] = useState<Lang>("en");
  useEffect(() => {
    const update = () => setDisplayLang((i18n.language === "nb" ? "nb" : "en") as Lang);
    update();
    i18n.on("languageChanged", update);
    return () => {
      i18n.off("languageChanged", update);
    };
  }, []);
  useEffect(() => {
    supabase.auth.getSession().then(({ data }) => {
      if (data.session) navigate({ to: "/dashboard", replace: true });
    });
  }, [navigate]);

  return (
    <div className="min-h-screen bg-background text-foreground" suppressHydrationWarning>
      <header
        className="max-w-md mx-auto px-5 pt-8 flex items-center justify-between"
        suppressHydrationWarning
      >
        <span className="font-display font-extrabold tracking-tighter text-xl">VISIKT</span>
        <div className="flex items-center gap-3">
          <div className="flex items-center text-[10px] font-mono uppercase tracking-widest border border-border rounded-full overflow-hidden">
            <button
              type="button"
              onClick={() => setLanguage("en")}
              aria-label="English"
              className={
                "px-2.5 py-1 transition-colors " +
                (displayLang === "en" ? "bg-foreground text-background" : "text-muted-foreground")
              }
            >
              EN
            </button>
            <button
              type="button"
              onClick={() => setLanguage("nb")}
              aria-label="Norsk bokmål"
              className={
                "px-2.5 py-1 transition-colors " +
                (displayLang === "nb" ? "bg-foreground text-background" : "text-muted-foreground")
              }
            >
              NB
            </button>
          </div>
          <Link
            to="/auth"
            className="text-xs font-mono uppercase tracking-widest text-muted-foreground hover:text-foreground"
          >
            {landingT("landing.login")}
          </Link>
        </div>
      </header>

      <main className="max-w-md mx-auto px-5 pt-16 pb-16 space-y-12" suppressHydrationWarning>
        <section className="animate-entry">
          <p className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground mb-4">
            {landingT("landing.eyebrow")}
          </p>
          <h1 className="text-4xl font-display font-bold tracking-tight leading-[1.05] text-balance">
            {landingT("landing.headline")}
          </h1>
          <p className="mt-5 text-base text-muted-foreground leading-relaxed">
            {landingT("landing.body")}
          </p>
          <div className="mt-8 flex flex-col gap-3">
            <Link
              to="/register"
              className="w-full py-3.5 text-center rounded-md bg-accent text-primary font-medium text-sm"
            >
              {landingT("landing.getStarted")}
            </Link>
            <Link
              to="/auth"
              className="w-full py-3.5 text-center rounded-md border border-border text-foreground font-medium text-sm"
            >
              {landingT("landing.haveAccount")}
            </Link>
          </div>
        </section>

        <section className="border-t border-border pt-10 animate-entry [animation-delay:120ms]">
          <h2 className="font-display font-bold text-sm tracking-tight mb-6">
            {landingT("landing.pillars")}
          </h2>
          <div className="space-y-8">
            <Pillar
              n="01"
              title={landingT("landing.pillar1Title")}
              body={landingT("landing.pillar1Body")}
            />
            <Pillar
              n="02"
              title={landingT("landing.pillar2Title")}
              body={landingT("landing.pillar2Body")}
            />
          </div>
        </section>

        <PublicFooter />
      </main>
    </div>
  );
}

function Pillar({ n, title, body }: { n: string; title: string; body: string }) {
  return (
    <div className="flex gap-5">
      <span className="font-mono text-[10px] text-muted-foreground pt-1">{n}</span>
      <div className="flex-1">
        <h3 className="font-display font-bold text-lg tracking-tight mb-1.5">{title}</h3>
        <p className="text-sm text-muted-foreground leading-relaxed">{body}</p>
      </div>
    </div>
  );
}
